import Text from 'components/Text/Text';
import React, { useContext, useState } from 'react';
import { RequestEntity, UserRoles, IItemUsers } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { IUsersContext, UsersContext } from '../../../../../usersState/usersContext';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import DataController from 'components/DataController/DataController';
import Info from './components/Info';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IItem {
	data: IItemUsers;
	index: number;
}

const Item: React.FC<IItem> = ({ data, index }) => {
	const { setIDForDeletion } = useContext<IUsersContext>(UsersContext);
	const [isOpenInfo, setIsOpenInfo] = useState<boolean>(false);
	const hasEditAccess = useRoleAccess([UserRoles.SUPER_USER]);
	const hasDeleteAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);

	const isDeleted = !!data.deletedDate;

	const containerClasses = classNames(style.container, {
		[style.openInfo]: isOpenInfo,
		[style.itemDeleted]: isDeleted,
	});
	const nameRowClasses = classNames(style.row, style.name);
	const emailRowClasses = classNames(style.email, style.row);
	const roleRowClasses = classNames(style.role, style.row, style.list);
	const actionRowClasses = classNames(style.action, style.row, { [style.itemDeleted]: isDeleted });
	const iconClasses = classNames(style.icon, { [style.itemDeleted]: isDeleted });

	const handleClickInfo = () => {
		setIsOpenInfo(!isOpenInfo);
	};

	const handleSetIdForDeletion = () => {
		setIDForDeletion(data.id);
	};

	const getDefaultAvatar = () => {
		return (
			<div className={style.avatarContainer}>
				<svg>
					<use xlinkHref={`${breadcrumbs}#userAvatar${data.roleName}`} />
				</svg>
			</div>
		);
	};

	const defaultAvatar = getDefaultAvatar();

	return (
		<div data-test={`Item-${index}`}>
			<div className={containerClasses}>
				<div className={nameRowClasses}>
					<div className={style.imgWrapper}>
						{isDeleted && <div className={style.imageOverlay} />}
						{data.filePath ? (
							<ImgUploaderByEntityId
								resource={RequestEntity.Users}
								entityID={data.id}
								photoSize="Small"
								loaderSize="small"
							/>
						) : (
							defaultAvatar
						)}
					</div>
					<Text
						className={style.firstName}
						translationKey={`${data.firstName} ${data.surName}`}
						theme={isDeleted ? 'error' : 'neutralDark'}
						withTooltip={true}
						data-test="userName"
					/>
				</div>
				<Text
					className={emailRowClasses}
					translationKey={data.email}
					theme={isDeleted ? 'error' : 'neutralDark'}
					withTooltip={true}
					data-test="email"
				/>
				<div className={roleRowClasses}>
					<svg className={iconClasses}>
						<use xlinkHref={`${breadcrumbs}#userRole${data.roleName}`} />
					</svg>
					<Text
						translationNameSpace="listing"
						translationKey={data.roleName}
						theme={isDeleted ? 'error' : 'neutralDark'}
						withTooltip={true}
						data-test="role"
					/>
				</div>
				<div className={actionRowClasses}>
					<DataController
						onClickInfo={handleClickInfo}
						onClickDelete={hasDeleteAccess ? handleSetIdForDeletion : undefined}
						URLWithIDToEdit={hasEditAccess ? `/users/edit?userID=${data.id}` : undefined}
						size="smallBtn"
						isOpenInfo={isOpenInfo}
					/>
				</div>
			</div>
			<Info isOpenInfo={isOpenInfo} data={data} />
		</div>
	);
};

export default Item;
